import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Modal from "../../../components/base/Modal";
import { Button, EmptyState, Input } from "../../../components/ui";
import TabLoader from "./TabLoader";
import type { NotificationUser } from "../types";

export interface NotificationUsersModalProps {
  available: NotificationUser[];
  isLoading: boolean;
  isSaving: boolean;
  onClose: () => void;
  onAdd: (users: NotificationUser[]) => void;
}

export default function NotificationUsersModal({
  available,
  isLoading,
  isSaving,
  onClose,
  onAdd,
}: NotificationUsersModalProps) {
  const { t } = useTranslation("common");

  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<string[]>([]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (term.length === 0) return available;

    return available.filter(
      (user) =>
        user.name.toLowerCase().includes(term) ||
        (user.email ?? "").toLowerCase().includes(term) ||
        (user.roleName ?? "").toLowerCase().includes(term),
    );
  }, [available, search]);

  const toggle = (id: string) =>
    setPicked((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));

  const confirm = () => onAdd(available.filter((user) => picked.includes(user.userId)));

  return (
    <Modal
      title={t("monitoreoIa.notifications.addUserTitle")}
      onClose={onClose}
      width="w-full max-w-xl"
      content={
        <div className="space-y-4">
          <p className="text-xs text-content-secondary">
            {t("monitoreoIa.notifications.addUserDescription")}
          </p>

          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("monitoreoIa.notifications.searchUser")}
            icon={<i className="ri-search-line" />}
            containerClassName="w-full"
          />

          {isLoading ? (
            <TabLoader label={t("monitoreoIa.notifications.loading")} />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon="ri-user-search-line"
              title={
                search
                  ? t("monitoreoIa.notifications.noUsers")
                  : t("monitoreoIa.notifications.allUsersAdded")
              }
            />
          ) : (
            <ul className="max-h-[46vh] space-y-2 overflow-y-auto max-sm:max-h-[52vh] max-sm:space-y-2.5">
              {filtered.map((user) => {
                const isPicked = picked.includes(user.userId);

                return (
                  <li key={user.userId}>
                    <button
                      type="button"
                      onClick={() => toggle(user.userId)}
                      className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors max-sm:min-h-16 max-sm:py-3 ${
                        isPicked
                          ? "border-primary-500/50 bg-primary-500/10"
                          : "border-line bg-surface hover:border-primary-500/40"
                      }`}
                    >
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 max-sm:h-6 max-sm:w-6 ${
                          isPicked ? "border-primary-500 bg-primary-500" : "border-line"
                        }`}
                      >
                        {isPicked && <i className="ri-check-line text-[10px] text-on-primary max-sm:text-xs" />}
                      </span>

                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-500/10 text-[13px] font-bold text-primary-500">
                        {user.name.charAt(0)}
                      </span>

                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-semibold text-content">
                          {user.name}
                        </span>
                        {user.roleName && (
                          <span className="block truncate text-[11px] text-content-secondary max-sm:text-xs">
                            {user.roleName}
                          </span>
                        )}
                        <span className="block truncate text-[11px] text-content-faint max-sm:text-xs">
                          {user.email ?? t("monitoreoIa.notifications.noEmail")}
                        </span>
                      </span>

                      <span className="flex shrink-0 items-center gap-1 max-sm:hidden">
                        {user.channels
                          .filter((channel) => channel.available)
                          .map((channel) => (
                            <i
                              key={channel.id}
                              title={channel.label}
                              className={`${channel.icon} text-[13px] text-content-faint`}
                            />
                          ))}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="grid grid-cols-2 gap-3 border-t border-line pt-4">
            <Button variant="secondary" fullWidth shape="pill" className="max-sm:min-h-11" onClick={onClose}>
              {t("monitoreoIa.actionsCommon.cancel")}
            </Button>
            <Button
              fullWidth
              className="max-sm:min-h-11"
              disabled={picked.length === 0 || isSaving}
              loading={isSaving}
              onClick={confirm}
            >
              {t("monitoreoIa.notifications.addSelected", { count: picked.length })}
            </Button>
          </div>
        </div>
      }
    />
  );
}
